/**
 * hq/shared/types.ts — the domain + API contract shared by the Worker and web app.
 * Single source of truth; both sides import from here.
 */

export type AgentId = 'emma' | 'milo' | 'leo' | 'quinn' | 'iris' | 'nora' | 'grace';
export type AgentStatus = 'idle' | 'working' | 'waiting_founder' | 'blocked' | 'off';
export type OrbMotion = 'original' | 'broadcast' | 'builder' | 'radar' | 'aperture' | 'voice' | 'drift';

export type MessageKind =
  | 'chat' | 'task' | 'correction' | 'knowledge' | 'question'
  | 'signal' | 'standup' | 'incident' | 'approval' | 'result'
  // Work Board thread entries (thread_id = card id):
  | 'card-comment' | 'change-request' | 'card-activity';

export type TaskStatus = 'queued' | 'claimed' | 'running' | 'done' | 'failed' | 'awaiting_founder';
export type ApprovalStatus = 'pending' | 'approved' | 'changes_requested' | 'expired';
export type NodeStatus = 'active' | 'testing' | 'retired';
export type Severity = 'low' | 'medium' | 'high' | 'critical';

export interface Agent {
  id: AgentId;
  name: string;
  role: string;
  color: string;
  motion: OrbMotion;
  remit_md?: string | null;
  schedule_md?: string | null;
  avatar_urls?: string | null;
  status: AgentStatus;
  kill_switch: number;
  sort_order: number;
}

export interface Message {
  id: string;
  thread_id: string;
  author_type: 'founder' | 'agent' | 'system';
  author_id: string;
  body_md: string;
  kind: MessageKind;
  refs?: unknown;
  depth: number;
  created_at: string;
}

export interface Task {
  id: string;
  agent_id: AgentId | 'company'; // 'company' = founder knowledge filing / brand patches (no agent owner)
  source_message_id?: string | null;
  kind: string;
  title: string;
  payload?: unknown;
  status: TaskStatus;
  result?: unknown;
  claimed_by?: string | null;
  created_at: string;
  claimed_at?: string | null;
  finished_at?: string | null;
}

export interface QuinnReport {
  passed: boolean;
  checks: Array<{ label: string; ok: boolean; detail?: string }>;
  video_key?: string;
  screenshots?: string[];
}

export interface Approval {
  id: string;
  agent_id: AgentId;
  title: string;
  summary_md?: string | null;
  preview_assets?: string[]; // R2 keys
  action: unknown; // the exact sandboxed action
  quinn_report?: QuinnReport | null;
  status: ApprovalStatus;
  decided_by?: string | null;
  overridden_by?: string | null;
  override_reason?: string | null;
  decided_at?: string | null;
  executed_at?: string | null;
  execution_result?: unknown;
  verify_after?: QuinnReport | null;
  deadline_at?: string | null;
  created_at: string;
}

export interface Standup {
  id: string;
  agent_id: AgentId;
  date: string;
  yesterday_md?: string | null;
  numbers?: Record<string, unknown> | null;
  wins_md?: string | null;
  struggles_md?: string | null;
  lessons?: string[] | null;
  today_md?: string | null;
  blocked_on_founder_md?: string | null;
  created_at: string;
}

export interface Run {
  id: string;
  agent_id?: AgentId | null;
  pass: string;
  task_id?: string | null;
  status: 'running' | 'ok' | 'error';
  duration_ms?: number | null;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  failures: number;
  evidence?: unknown;
  created_at: string;
}

export interface Incident {
  id: string;
  agent_id?: AgentId | null;
  severity: Severity;
  title: string;
  evidence_md?: string | null;
  proposed_fix_md?: string | null;
  status: 'open' | 'ack' | 'resolved';
  created_at: string;
  /** Stable key for a recurring problem. A repeat with the same open key
   * refreshes that incident instead of opening a new one. Null = never merged. */
  dedupe_key?: string | null;
  /** Latest sighting (created_at stays the first). Equals created_at on a one-off. */
  last_seen_at?: string | null;
  /** How many times this recurring problem has been reported. 1 = seen once. */
  times_seen?: number;
}

export interface BrainNode {
  id: string;
  agent_id: AgentId | 'company';
  domain?: string | null;
  title: string;
  status: NodeStatus;
  source?: string | null;
  confidence?: string | null;
  path?: string | null;
  links?: string[] | null;
  body?: string | null; // markdown body (GET /brains/node/:id only — graph.json stays lean)
  used_by?: string[] | null; // agent ids whose brains link here (company nodes)
  created_at: string;
  // --- Brain v3 organisation (D1-authoritative; survives git re-sync) ---
  category_id?: string | null;
  updated_at?: string; // bumps on founder edit / category move
  founder_edited?: number; // 1 = founder rewrote title/body — sync must not undo
}

// --- Brain v3: the Knowledge Hub (build-spec/BRAIN-V3-SPEC.md) -----------------

export interface BrainCategory {
  id: string;
  agent_id: AgentId | 'company';
  name: string;
  slug: string;
  description?: string | null;
  parent_id?: string | null;
  sort_order: number;
  auto_generated: number; // 1 = description auto-written; founder edits flip it to 0
  created_at: string;
  updated_at: string;
}

/** One category card in the hierarchy, with its sub-categories nested. */
export interface BrainCategoryNode extends BrainCategory {
  item_count: number; // items directly in this category
  total_count: number; // items here + in every sub-category
  last_updated: string | null; // newest item update here or below
  children: BrainCategoryNode[];
}

/** GET /brains/:agent/hierarchy — the hub's primary navigation. */
export interface BrainHierarchy {
  agent: AgentId | 'company';
  categories: BrainCategoryNode[];
  uncategorized_count: number; // items not filed anywhere yet
  total_items: number;
}

/** Where a search hit landed, as offsets into the RETURNED strings. */
export interface BrainMatch {
  field: 'title' | 'takeaway' | 'source' | 'category';
  start: number;
  length: number;
}

/** One row in the hub's item lists (lean — the item page loads the full node). */
export interface BrainItem {
  id: string;
  title: string;
  status: NodeStatus;
  source?: string | null;
  confidence?: string | null;
  category_id?: string | null;
  takeaway: string; // one-line body excerpt (centered on the match when searching)
  created_at: string;
  updated_at: string;
  matches?: BrainMatch[];
}

export interface BrainItemGroup {
  category: { id: string; name: string } | null; // null = "Not filed yet"
  items: BrainItem[];
}

/** GET /brains/:agent/items?category=&q= — grouped, highlight-ready results. */
export interface BrainItemsResponse {
  agent: AgentId | 'company';
  q: string;
  total: number;
  groups: BrainItemGroup[];
}

/** GET /brains/:agent/stats?from=&to= — header stats strip, all real numbers. */
export interface BrainStats {
  agent: AgentId | 'company';
  from: string; // YYYY-MM-DD inclusive
  to: string; // YYYY-MM-DD inclusive
  cost_usd: number; // sum of runs.cost_usd in range
  cost_series: Array<{ day: string; cost: number; runs: number }>; // zero-filled, oldest→newest
  items_total: number;
  items_new: number; // created in range
  category_count: number;
  top_sources: Array<{ source: string; count: number }>; // node origins + run passes, merged
}

export interface BrainChatCitation {
  id: string;
  title: string;
  category_id?: string | null;
}

/** An organisation tidy-up the chat may PROPOSE — never auto-applied. */
export type BrainChatAction =
  | { kind: 'move_item'; node_id: string; node_title: string; category_id: string; category_name: string }
  | { kind: 'rename_category'; category_id: string; current_name: string; name: string };

/** POST /brains/:agent/chat — grounded answer with receipts. */
export interface BrainChatResponse {
  answer_md: string; // keeps [item:id] markers so the client renders citation chips
  citations: BrainChatCitation[];
  proposed_actions: BrainChatAction[];
  turns_left_today: number;
}

export interface BrainGraph {
  agent: AgentId | 'company';
  center: { id: string; title: string; count: number };
  hubs: Array<{ id: string; domain: string; count: number }>;
  nodes: BrainNode[];
  edges: Array<{ from: string; to: string }>;
}

export interface Cost {
  id: string;
  date: string;
  agent_id?: AgentId | null;
  provider: 'anthropic' | 'elevenlabs' | 'higgsfield' | 'resend';
  usd: number;
  units?: unknown;
  created_at: string;
}

export interface KnowledgeAsset {
  id: string;
  kind: 'image' | 'video' | 'logo' | 'doc' | 'note';
  title: string;
  r2_key?: string | null;
  repo_path?: string | null;
  tags?: string[] | null;
  routed_to?: string[] | null; // ['shared' | agent ids]
  uploaded_by?: string | null;
  created_at: string;
}

export interface SafeModeState {
  on: boolean;
  guards: Array<{ key: string; label: string; tripped: boolean }>;
  blocked_count: number;
  last_block?: { guard: string; message: string; created_at: string } | null;
}

/** One calendar day of run aggregates — the real series behind the stat sparklines. */
export interface DashboardDailyPoint {
  day: string; // 'YYYY-MM-DD' (UTC)
  runs: number;
  tokens: number; // tokens_in + tokens_out
  cost: number; // USD
  gaunt_ok: number; // quinn* runs that passed
  gaunt_total: number; // quinn* runs total
}

export interface DashboardStats {
  tokens_24h: number;
  cost_today: number;
  /** Quinn-gauntlet pass rate (0–1), or null when NO gauntlet has run yet.
   *  null ≠ 0: null means "no data" (show —), a real 0 means gauntlets ran and
   *  all failed (show 0.0%). Never conflate the two on the founder's dashboard. */
  gauntlet_pass_rate: number | null;
  runs_today: number;
  cost_forecast_30d: number;
  safe_mode: SafeModeState;
  /** Dense last-14-day daily series (oldest→newest, missing days zero-filled). Real
   *  history behind each stat card's sparkline — no more decorative fabrication. */
  series: DashboardDailyPoint[];
}

/** One red flag an agent surfaced in a task result's `red[]`. Extracted from the
 *  LATEST run of each (agent, kind) so a fixed issue naturally disappears —
 *  current state of the funnel/creative, not a growing history. */
export interface TeamFinding {
  agent_id: AgentId | 'company';
  kind: string; // the task kind that produced it, e.g. 'funnel-health'
  title: string; // the task title
  text: string; // the finding, in plain English
  url?: string | null; // the page it's about, if the result carried one
  receipt_key?: string | null; // R2 key of the screenshot proof this finding is about, if the agent captured one
  task_id: string;
  finished_at: string; // when this finding was produced (the task's finish time)
  blocked?: boolean; // true = an agent is parked awaiting_founder; `text` is what unblocks it
  // For a blocked finding, the raw `text` is often developer jargon (env-var names,
  // an IP-whitelist string). `plain` is the same blocker rewritten as one plain-English
  // action a non-technical founder can actually take; `detail` is the exact value to
  // act on (an IP, a key name), shown as a copyable receipt. Both absent on red flags.
  plain?: string | null;
  detail?: string | null;
}

// --- the Work Board (build-spec/BOARD-SPEC.md) --------------------------------

/** The six fixed pipeline columns, in order. */
export type CardColumnKey =
  | 'backlog' | 'in_progress' | 'qa' | 'human_review' | 'scheduled_live' | 'done';

/** Column order + founder-visible labels ("Human review", never "Founder review"). */
export const CARD_COLUMNS: ReadonlyArray<{ key: CardColumnKey; label: string }> = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'qa', label: 'Quinn QA' },
  { key: 'human_review', label: 'Human review' },
  { key: 'scheduled_live', label: 'Scheduled · Live' },
  { key: 'done', label: 'Done' },
];

export const CARD_WORK_TYPES = ['campaign', 'flow', 'cro_test', 'page', 'creative', 'other'] as const;
export type CardWorkType = (typeof CARD_WORK_TYPES)[number];

export const CARD_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type CardPriority = (typeof CARD_PRIORITIES)[number];

/** Founder-visible priority labels for the drawer chips. */
export const CARD_PRIORITY_LABELS: Record<CardPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

/**
 * The fixed 10-color tag pool (BOARD V2 §B). New tags are auto-assigned the
 * next color by creation count — founders never pick colors, so the board
 * stays coherent.
 */
export const TAG_COLOR_POOL = [
  '#5BD1F5', // sky
  '#4D7CFF', // blue
  '#46E0A3', // mint
  '#9D8CFF', // violet
  '#FF6B5E', // coral
  '#FFC24D', // amber
  '#F0ABFC', // pink
  '#5EEAD4', // teal
  '#FDBA74', // orange
  '#8FA3B0', // slate
] as const;

/** A custom board tag. created_by logs who coined it (tag discipline audit). */
export interface Tag {
  id: string;
  name: string;
  color: string;
  created_by: string; // 'founder:<name>' | 'agent:<id>'
  created_at: string;
  /** GET /board/tags only: how many live cards wear it. */
  count?: number;
}

/** Everything a card points at. Extra keys are allowed and preserved. */
export interface CardRefs {
  task_ids?: string[];
  approval_id?: string;
  /** R2 key of the rendered preview (email HTML, page screenshot, markdown). */
  preview_asset?: string;
  klaviyo_ids?: Record<string, string>;
  /** The exact sandboxed action this card proposes (Quinn turns it into an
   *  approval when she passes the card to Human review). */
  action?: unknown;
  /** The brief the draft was built from, so a revision can rebuild honestly. */
  brief?: unknown;
  [key: string]: unknown;
}

export interface Card {
  id: string;
  agent_id: AgentId;
  title: string;
  summary_md?: string | null;
  work_type: CardWorkType;
  column_key: CardColumnKey;
  /** REAL — drag-to-reorder writes the midpoint between neighbours. */
  sort_order: number;
  due_date?: string | null; // YYYY-MM-DD (calendar view)
  ice_score?: number | null; // cro_test backlog chip
  refs?: CardRefs | null;
  result_md?: string | null; // receipts (done cards)
  created_by: string; // 'founder:<name>' | 'agent:<id>'
  created_at: string;
  updated_at: string;
  moved_at: string;
  // --- Board v2 (addendum §A–C) ---
  /** Who owns the work: an agent id, a founder email, or null (unassigned). */
  assignee?: string | null;
  /** Soft archive — set = hidden everywhere unless "Show archived" is on. */
  archived_at?: string | null;
  priority?: CardPriority | null;
  /** Drawer Description tab (markdown; founder AND agents may edit). */
  description_md?: string | null;
  /** Hydrated on board/card reads. */
  tags?: Tag[];
  /** GET /board only: card-comment + change-request count for the card face. */
  comment_count?: number;
}

export interface BoardColumnData {
  key: CardColumnKey;
  label: string;
  cards: Card[];
}

/** GET /board — all six columns, always present, cards sorted by sort_order. */
export interface BoardData {
  columns: BoardColumnData[];
}

export interface Founder {
  email: string;
  name: string;
  phone?: string | null;
}

/** GET /admin/runner — heartbeat freshness (online = seen within 15 min). */
export interface RunnerStatus {
  last_heartbeat: string | null;
  info?: unknown;
  online: boolean;
}

// --- auth ---
export interface Me {
  email: string;
  name: string;
}

/** GET /auth/asset-token — short-lived (15 min) token for GET /assets/* URLs. */
export interface AssetToken {
  token: string;
  exp: number; // unix seconds
}
